// src/features/context/LayoutResolver.tsx
// Input an entity, resolve its Context Panel layout, show the chain as a table
// with a raw-JSON disclosure. Also lets an admin fire a synthetic behavior
// event at the resolved entity to exercise the scoring pipeline.
// See docs/ux/context-ia.md section 1.

import { useState } from 'react';
import { useI18n, useContext as useContextStore } from '@react/hooks';
import { Button, Input, ErrorAlert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import type { ContextLayout } from '@core/types/context';
import { enumSuffix } from './logic';

export function LayoutResolver() {
  const { t } = useI18n();
  const store = useContextStore();
  const [entityType, setEntityType] = useState('task');
  const [entityId, setEntityId] = useState('');
  const [layout, setLayout] = useState<ContextLayout | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [trackedKey, setTrackedKey] = useState<string | null>(null);
  const [isTracking, setIsTracking] = useState(false);

  const handleResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!entityType.trim() || !entityId.trim()) return;
    setIsResolving(true);
    try {
      const r = await store.GetContextLayout(entityType.trim(), entityId.trim());
      setLayout(r.layout ?? null);
    } finally {
      setIsResolving(false);
    }
  };

  const handleTrack = async (sectionKey: string) => {
    setTrackedKey(sectionKey);
    setIsTracking(true);
    try {
      await store.TrackBehavior({
        entity: { type_id: '', type_name: entityType, id: entityId, platform_id: '', tenant_id: '', product_id: '' },
        events: [{ section_key: sectionKey, entity_type: entityType, event_type: 'BEHAVIOR_EVENT_TYPE_EXPAND', timestamp_ms: Date.now() }],
      });
    } finally {
      setIsTracking(false);
      setTrackedKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleResolve} className="flex flex-wrap items-end gap-3">
        <Input label={t('context.layout.entityType')} value={entityType} onChange={(e) => setEntityType(e.target.value)} className="w-40" />
        <Input label={t('context.layout.entityId')} value={entityId} onChange={(e) => setEntityId(e.target.value)} className="w-64" required />
        <Button type="submit" size="sm" disabled={isResolving}>{isResolving ? t('common.loading') : t('context.layout.resolve')}</Button>
      </form>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {layout && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('context.layout.summary', { count: layout.sections.length, hidden: layout.hidden_count })}
            </p>
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => setShowRaw((v) => !v)}>
              {showRaw ? t('context.layout.hideRaw') : t('context.layout.showRaw')}
            </button>
          </div>

          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('context.layout.col.key')}</TableHead>
                <TableHead>{t('context.layout.col.type')}</TableHead>
                <TableHead>{t('context.layout.col.displayName')}</TableHead>
                <TableHead>{t('context.layout.col.mode')}</TableHead>
                <TableHead>{t('context.layout.col.score')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {layout.sections.length === 0 ? (
                <TableEmpty colSpan={6} message={t('context.layout.noSections')} />
              ) : (
                layout.sections.map((s) => (
                  <TableRow key={s.key}>
                    <TableCell className="font-mono text-xs">{s.key}</TableCell>
                    <TableCell>{t(`context.layout.sectionType.${enumSuffix(s.type, 'CONTEXT_SECTION_TYPE_')}`)}</TableCell>
                    <TableCell>{s.display_name}</TableCell>
                    <TableCell>
                      <StatusBadge variant="default" label={t(`context.layout.displayMode.${enumSuffix(s.display_mode, 'CONTEXT_DISPLAY_MODE_')}`)} size="sm" />
                    </TableCell>
                    <TableCell>{s.debug_score}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" disabled={isTracking && trackedKey === s.key} onClick={() => handleTrack(s.key)}>
                        {isTracking && trackedKey === s.key ? t('common.loading') : t('context.layout.sendTestEvent')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {showRaw && (
            <pre className="max-h-96 overflow-auto rounded-md bg-muted/40 p-3 text-xs">{JSON.stringify(layout, null, 2)}</pre>
          )}
        </div>
      )}

      {!layout && !isResolving && (
        <p className="text-sm text-muted-foreground">{t('context.layout.empty')}</p>
      )}
    </div>
  );
}
